/**
 * `?port=` — putting one of the fourteen simulated systems into a failure mode.
 *
 * ```
 * ?port=companyRegistry:degraded   the registry answers "not active"  → CUI invalid
 * ?port=companyRegistry:fail       the registry does not answer       → CUI service down
 * ?port=customer:degraded          no account to order against        → no active OCN
 * ?port=catalog:degraded           the segment has no offers          → no eligible offers
 * ?port=activation:fail            the line never activates           → compensation (Q-22)
 * ?port=stock:degraded,otp:slow    more than one at a time
 * ?port=off                        every port back to normal
 * ```
 *
 * Ports: session, customer, companyRegistry, internalEligibility, catalog, pricing,
 * otp, stock, installments, contract, payment, fulfilment, activation, notification.
 * Modes: normal, fail, degraded, slow. Unrecognised pairs are dropped with a console
 * warning rather than ignored silently — a typo must not look like a system that
 * refuses to break.
 *
 * ### Why four of the blocked triggers have no other entrance
 *
 * The personas reach five of the fourteen triggers and the fixtures reach four more.
 * `CUI_VALIDATION_FAILED`, `CUI_SERVICE_UNAVAILABLE`, `NO_ACTIVE_OCN` and
 * `NO_ELIGIBLE_OFFERS` are not properties of a customer — they are a system saying
 * no, or saying nothing. A persona whose only purpose was to make a registry fail
 * would be a fixture that describes an outage, and the fixtures are review material
 * Orange reads as a description of its own customers. So those four are reached by
 * breaking the system instead, which is also what actually happens.
 *
 * `?rate=` is the same mechanism narrowed to one screen and one port, kept separate
 * because it also picks which of S-12's three undefined treatments applies. Nothing
 * stops `?port=installments:degraded` from being written by hand; it just produces a
 * decline with whichever treatment is currently set.
 *
 * ### Persistence, against the adapter's own rule
 *
 * `AdapterModes` is deliberately not persisted — a forced failure switched on to show
 * a colleague should not still be on the next morning. The switch string is, in
 * `sessionStorage`, for the same reason `?rate=` is: a query string does not survive
 * the first in-app navigation, and the eligibility chain runs on the screen after the
 * one you can type a URL on. It dies with the tab, `clearAdapterModeSwitch()` drops
 * it, and `DemoControls.reset()` clears the modes themselves.
 *
 * POC scaffolding. Production has no switch for which of its systems is down.
 */
import { inject, provideAppInitializer } from '@angular/core';
import type { EnvironmentProviders } from '@angular/core';
import { ALL_PORTS } from '../../mock-bff/adapters/adapter-runtime';
import { MockBackend } from '../../mock-bff/mock-backend';

const STORAGE_KEY = 'pre2post.poc.port.v1';

/** Applies a held or URL-supplied switch at startup. */
export function provideAdapterModeSwitch(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const pairs = read();
    if (pairs.length === 0) return;

    // Resolved through the injector rather than constructed: a second `MockBackend`
    // would be a second set of adapters, and the modes would be set on the wrong ones.
    const backend = inject(MockBackend);

    for (const pair of pairs) {
      const [port = '', mode = ''] = pair.split(':');
      // The same `applyMode` the `demoSetAdapterMode` mutation runs, through
      // `DemoControls`, so a URL and the panel cannot diverge on what they accept.
      if (!backend.controls.setAdapterMode(port.trim(), mode.trim())) warn(pair);
    }
  });
}

/** Holds the choice for the rest of the tab's life. Persists only; see the header. */
export function setAdapterModeSwitch(value: string): void {
  try {
    const pairs = parseAdapterModeSwitch(value);
    if (pairs.length === 0) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, pairs.join(','));
  } catch {
    // Private browsing. The switch applies to this page load and no further.
  }
}

export function clearAdapterModeSwitch(): void {
  setAdapterModeSwitch('off');
}

/**
 * What is held right now, as `port:mode` pairs.
 *
 * The panel does not use this to display the live modes — `AdapterModes.active()` is the
 * truth for that, and the two can legitimately differ: `demoSetAdapterMode` and
 * `?rate=fail` both change a mode without touching this string. This is what a reload
 * would restore, which is a different and also useful fact.
 */
export function currentAdapterModeSwitch(): readonly string[] {
  try {
    return parseAdapterModeSwitch(sessionStorage.getItem(STORAGE_KEY) ?? '');
  } catch {
    return [];
  }
}

/**
 * Splits the value into `port:mode` pairs without judging them.
 *
 * Validation belongs to `applyMode`, which owns the two vocabularies; duplicating the
 * port list here would mean a fifteenth port being accepted by one and rejected by the
 * other. `off` is the word for "nothing forced", so clearing a held switch does not
 * require knowing what is in it.
 */
export function parseAdapterModeSwitch(value: string): readonly string[] {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === 'off') return [];
  return trimmed
    .split(',')
    .map((pair) => pair.trim())
    .filter((pair) => pair !== '');
}

/** The URL wins over the held value, and replaces it. */
function read(): readonly string[] {
  try {
    const fromUrl = new URLSearchParams(location.search).get('port');
    if (fromUrl !== null) {
      // Persisted even when empty: `?port=off` has to be able to clear a held one.
      setAdapterModeSwitch(fromUrl);
      return parseAdapterModeSwitch(fromUrl);
    }
    return parseAdapterModeSwitch(sessionStorage.getItem(STORAGE_KEY) ?? '');
  } catch {
    return [];
  }
}

const warn = (pair: string): void => {
  console.warn(
    `[dev] ?port= ignored "${pair}". Expected <port>:<normal|fail|degraded|slow>, ` +
      `where port is one of ${ALL_PORTS.join(', ')}.`,
  );
};
